import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

/* ---------------------------------------------------------------------------
 * Ambient globals — Potree ships as plain old window globals (window.THREE,
 * window.Potree, window.TWEEN). We don't import them; the loader below adds
 * <script> tags pointing at the static copy in `public/Potree_1.8.2/`.
 * --------------------------------------------------------------------------- */
/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    THREE: any;
    Potree: any;
    TWEEN: any;
    jQuery?: any;
    $?: any;
    viewer?: any;
  }
}

export type PotreeNavigationMode = "orbit" | "earth" | "fly";
export type PotreeMeasureType = "point" | "distance" | "height" | "area" | "angle";
export type PotreeCameraView = "top" | "bottom" | "left" | "right" | "front" | "back";
export type PotreeProjection = "perspective" | "orthographic";

export interface PotreeViewerHandle {
  /** Pick the model surface at a canvas-pixel coordinate.
   *  Returns a `THREE.Vector3` (untyped here because THREE is loaded at
   *  runtime) or null if the ray missed the cloud. */
  pickAtPixel: (pixelX: number, pixelY: number) => any | null;
  /** Add a marker at a world point. `shape` selects the visual:
   *    - "sprite" (default): canvas-painted reticle, used by the measure tool.
   *    - "cube": colored box with white edge lines — used by asset tags.
   *    - "sphere": colored sphere with a soft outline — used by sensor tags.
   *  Returns the object so the caller can remove it or attach userData. */
  addPointMarker: (
    worldPos: any,
    color?: number,
    options?: { shape?: "sprite" | "cube" | "sphere" },
  ) => any;
  removeMarker: (obj: any) => void;
  addLine: (a: any, b: any, color?: number) => any;
  removeLine: (line: any) => void;
  /** Re-fit the camera to the cloud's bounding sphere. */
  recenter: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Animate the camera onto a world-space point. */
  zoomToPoint: (point: any) => void;
  /** Briefly scale a marker up and back down — used to draw attention to a
   *  tag the user just clicked in the sidebar. */
  highlightMarker: (obj: any, options?: { durationMs?: number; scale?: number }) => void;
  setFov: (degrees: number) => void;
  getFov: () => number;
  /** Project a world point into canvas-local pixel coordinates. `visible` is
   *  false when behind the camera or outside the frustum. */
  projectToScreen: (point: any) => { x: number; y: number; visible: boolean };
  /** Hand off to Potree's built-in measuring tool. The tool registers its
   *  own pointer listeners and handles every aspect of the measurement
   *  (markers, labels, distance/area/angle math). Returns the measurement
   *  object so callers can hook events if they want, or null if the viewer
   *  isn't ready. */
  startMeasurement: (type: PotreeMeasureType) => any | null;
  /** Remove every measurement Potree has drawn so far. */
  clearMeasurements: () => void;
  /** Rotate the camera so the chosen axis is pointing at the lens. Preserves
   *  the current zoom / pan target — does NOT refit the whole bound the way
   *  Potree's native set*View() does, which would jerk the user back to the
   *  full scene every time. */
  setCameraView: (dir: PotreeCameraView) => void;
  /** Switch between perspective and orthographic projection. */
  toggleProjection: () => void;
  /** Which projection mode the active camera is currently in. */
  getCameraProjection: () => PotreeProjection;
}

interface PotreeViewerProps {
  /** Absolute URL to the Potree v2 metadata.json (already includes the
   *  view-token segment). */
  viewUrl: string;
  navigationMode?: PotreeNavigationMode;
  pointBudget?: number;
  edlEnabled?: boolean;
  /** Click on the cloud — fires with the picked 3D world point, or null
   *  when the click landed on empty space. */
  onPick?: (point: any | null, pixel: { x: number; y: number }) => void;
  /** Click on a marker (e.g. a placed tag). When this fires, `onPick` does
   *  NOT — the marker takes precedence so clicking a tag selects it rather
   *  than placing a new one underneath. */
  onMarkerPick?: (marker: any) => void;
  onLoadError?: (message: string) => void;
  onLoadProgress?: (loaded: number, total: number) => void;
  /** Fired once the point cloud's first frame has actually rendered (i.e.
   *  the parent's loading overlay can be hidden). */
  onReady?: () => void;
  className?: string;
}

/* ---------------------------------------------------------------------------
 * Script loader — lazy-loads the Potree static assets bundled in `public/`.
 * Idempotent: multiple PotreeViewer mounts share a single load promise so
 * we don't waste bandwidth re-fetching scripts that are already on the page.
 * --------------------------------------------------------------------------- */
const POTREE_BASE = "/Potree_1.8.2";

// Potree's CSS plus the CSS for any UI widget lib whose JS we also load.
// Without spectrum.css / jquery-ui CSS some Potree-generated controls
// (color picker swatches, dialog frames) render unstyled — harmless but ugly.
const POTREE_CSS = [
  `${POTREE_BASE}/build/potree/potree.css`,
  `${POTREE_BASE}/libs/jquery-ui/jquery-ui.min.css`,
  `${POTREE_BASE}/libs/spectrum/spectrum.css`,
];

// Potree's bundled potree.js does NOT inline its transitive deps. Its
// internal modules (map.js, the 2D overview, the sidebar bootstrap, the
// `selectgroup` jQuery plugin, etc.) reference `proj4`, `ol`, `d3`,
// `i18next`, `jQuery.ui`, `spectrum`, and `jstree` at *module-init time*.
// If any of those globals is missing when potree.js evaluates, the whole
// factory throws partway through and `Potree` is left as `{}` — the
// `exports.Viewer = Viewer` assignment at the bottom of the bundle is
// never reached.
//
// We mirror the canonical example layout from examples/viewer.html exactly,
// in the same order. The only deviation: we add three.js separately (Potree
// bundles its own copy inside potree.js but only exposes `window.Potree`;
// our viewer code needs `window.THREE` for raycasting / marker geometry).
// three.js is loaded out-of-band by `ensureThree()` below so we can guarantee
// `window.THREE` is populated even when an environment quirk hides the UMD
// global. The remaining scripts are loaded sequentially.
const THREE_SCRIPT_URL = `${POTREE_BASE}/libs/three.js/build/three.js`;

const POTREE_SCRIPTS = [
  // jQuery — Potree's bundle uses `$` at module init time (e.g. the
  // `(function($) { $.fn.extend({ selectgroup: ... }) })` IIFE).
  `${POTREE_BASE}/libs/jquery/jquery-3.1.1.min.js`,
  // jQuery-UI plugins (.draggable, .resizable, dialog widgets) — Potree's
  // bundle wires these onto $.fn at init time. Missing this lib was the
  // root cause of the "Potree.Viewer is missing" error users hit on first
  // load.
  `${POTREE_BASE}/libs/jquery-ui/jquery-ui.min.js`,
  // Spectrum color picker — Potree calls `$.fn.spectrum` from its material
  // panel setup; the plugin must be registered before potree.js runs.
  `${POTREE_BASE}/libs/spectrum/spectrum.js`,
  // jsTree — Potree's scene-tree widget. Same story: registered on $.fn
  // at init time.
  `${POTREE_BASE}/libs/jstree/jstree.js`,
  // Misc helpers used during init.
  `${POTREE_BASE}/libs/other/BinaryHeap.js`,
  `${POTREE_BASE}/libs/tween/tween.min.js`,
  `${POTREE_BASE}/libs/d3/d3.js`,
  // proj4 + openlayers — needed by Potree's `map.js` at parse time.
  // Without proj4 the bundle throws "proj4 is not defined".
  `${POTREE_BASE}/libs/proj4/proj4.js`,
  `${POTREE_BASE}/libs/openlayers3/ol.js`,
  // i18next — used by Potree's string-resolution helpers.
  `${POTREE_BASE}/libs/i18next/i18next.js`,
  // Potree itself — must run last, after every dep above.
  `${POTREE_BASE}/build/potree/potree.js`,
];

/** Load three.js and guarantee `window.THREE` is populated.
 *
 *  three.js ships as a UMD bundle whose top-level wrapper decides whether to
 *  assign to `module.exports`, `define`, or `global.THREE` based on which
 *  globals exist at evaluation time. Most browsers reliably pick the global
 *  path, but we've seen environments (some build-time injected modules,
 *  certain extension shims) where neither path runs and `window.THREE` stays
 *  undefined despite the script tag firing `onload` cleanly.
 *
 *  Belt-and-braces approach: try a normal script tag first; if `window.THREE`
 *  is still missing after that, fetch the file ourselves and execute it via
 *  `new Function`, forcing UMD's "browser" branch by hiding `module`,
 *  `exports`, and `define` from the wrapper. */
async function ensureThree(): Promise<void> {
  if (typeof window.THREE !== "undefined") return;
  // Path A: ordinary <script> tag.
  if (!document.querySelector(`script[src="${THREE_SCRIPT_URL}"]`)) {
    await new Promise<void>((resolve, reject) => {
      const tag = document.createElement("script");
      tag.src = THREE_SCRIPT_URL;
      tag.async = false;
      tag.onload = () => resolve();
      tag.onerror = () => reject(new Error(`Failed to load ${THREE_SCRIPT_URL}`));
      document.head.appendChild(tag);
    });
  }
  if (typeof window.THREE !== "undefined") return;
  // Path B: fetch + inline-evaluate, forcing the UMD wrapper down the
  // browser-global branch. Wrapping the body in a function whose param list
  // shadows `module`, `exports`, and `define` is enough to make every UMD
  // wrapper we've seen pick the `global.THREE = {}` path.
  const res = await fetch(THREE_SCRIPT_URL);
  if (!res.ok) {
    throw new Error(`Failed to fetch ${THREE_SCRIPT_URL}: HTTP ${res.status}`);
  }
  const source = await res.text();
  // eslint-disable-next-line no-new-func
  new Function("module", "exports", "define", source).call(window, undefined, undefined, undefined);
  if (typeof window.THREE === "undefined") {
    throw new Error(
      "three.js evaluated but did not expose window.THREE. The file may be " +
      "corrupted — re-check public/Potree_1.8.2/libs/three.js/build/three.js.",
    );
  }
}

let potreeLoadPromise: Promise<void> | null = null;

/** True iff Potree.js evaluated cleanly and exposed its full API. The UMD
 *  wrapper sets `window.Potree = {}` BEFORE the factory body runs — so a
 *  missing transitive dep that throws inside the factory still leaves a
 *  non-null but empty `window.Potree`. Reaching for `Potree.Viewer` is the
 *  cheapest way to distinguish a healthy load from a broken one. */
function potreeIsHealthy(): boolean {
  return typeof window.Potree?.Viewer === "function";
}

/** Self-recovery for the "broken Potree from a previous load" trap.
 *  Removes every script/link tag we injected, wipes the half-initialised
 *  globals, and resets the cached load promise. The next loadPotree() call
 *  starts from a clean slate — no user-visible "hard reload" prompt needed. */
function resetPotreeGlobals() {
  for (const src of [...POTREE_SCRIPTS, THREE_SCRIPT_URL]) {
    document.querySelectorAll(`script[src="${src}"]`).forEach((el) => el.remove());
  }
  for (const href of POTREE_CSS) {
    document.querySelectorAll(`link[href="${href}"]`).forEach((el) => el.remove());
  }
  try { delete (window as Window & { Potree?: unknown }).Potree; } catch { /* noop */ }
  try { delete (window as Window & { THREE?: unknown }).THREE; } catch { /* noop */ }
  try { delete (window as Window & { TWEEN?: unknown }).TWEEN; } catch { /* noop */ }
  potreeLoadPromise = null;
}

function loadPotree(): Promise<void> {
  // Healthy cached load — short-circuit on remount.
  if (potreeLoadPromise && potreeIsHealthy()) return potreeLoadPromise;
  // A stale `Potree = {}` from a previously-broken evaluation. Browsers
  // won't re-execute existing <script> tags by URL alone, so we have to
  // tear down everything we injected and start over. This makes the
  // "hard reload" prompt obsolete — recovery happens silently.
  if (typeof window.Potree !== "undefined" && !potreeIsHealthy()) {
    resetPotreeGlobals();
  }
  potreeLoadPromise = (async () => {
    // CSS: side-effect <link> tags. Don't block on these — they're style only.
    for (const href of POTREE_CSS) {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
      }
    }
    // Scripts: load sequentially so dependencies resolve in order.
    for (const src of POTREE_SCRIPTS) {
      if (document.querySelector(`script[src="${src}"]`)) continue;
      await new Promise<void>((resolve, reject) => {
        const tag = document.createElement("script");
        tag.src = src;
        tag.async = false;
        tag.onload = () => resolve();
        tag.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(tag);
      });
    }
    // three.js — loaded via the dedicated helper so we can self-recover if
    // the UMD wrapper takes a non-global branch in the user's environment.
    // ensureThree() throws if both loading paths fail, with a diagnostic.
    await ensureThree();
    if (!potreeIsHealthy()) {
      // Last-ditch diagnostic. With the full canonical script list above
      // this should never fire in practice; if it does, an underlying
      // ReferenceError will already be in the console.
      throw new Error(
        "Potree library evaluated but Potree.Viewer is missing — check the " +
        "browser console for the underlying error.",
      );
    }
  })();
  // Reset the cache on rejection so a remount (after the user reloads or
  // fixes the underlying issue) can retry instead of being stuck with a
  // permanently-rejected promise. Also tear down the half-initialised
  // globals so the retry starts from a clean slate.
  potreeLoadPromise.catch(() => {
    resetPotreeGlobals();
  });
  return potreeLoadPromise;
}

/* ---------------------------------------------------------------------------
 * Component
 * --------------------------------------------------------------------------- */

export const PotreeViewer = forwardRef<PotreeViewerHandle, PotreeViewerProps>(
  function PotreeViewer(
    {
      viewUrl, navigationMode = "orbit", pointBudget = 5_000_000, edlEnabled = true,
      onPick, onMarkerPick, onLoadError, onLoadProgress, onReady, className,
    },
    ref,
  ) {
    // Keep latest callbacks reachable from the once-mounted pointerup handler.
    const onPickRef = useRef(onPick);
    const onMarkerPickRef = useRef(onMarkerPick);
    const onLoadErrorRef = useRef(onLoadError);
    const onLoadProgressRef = useRef(onLoadProgress);
    const onReadyRef = useRef(onReady);
    useEffect(() => { onPickRef.current = onPick; }, [onPick]);
    useEffect(() => { onMarkerPickRef.current = onMarkerPick; }, [onMarkerPick]);
    useEffect(() => { onLoadErrorRef.current = onLoadError; }, [onLoadError]);
    useEffect(() => { onLoadProgressRef.current = onLoadProgress; }, [onLoadProgress]);
    useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

    const renderAreaRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<any>(null);
    const pointcloudRef = useRef<any>(null);
    const markersGroupRef = useRef<any>(null);
    const linesGroupRef = useRef<any>(null);
    const navigationModeRef = useRef(navigationMode);
    const readyFiredRef = useRef(false);

    const [scriptsReady, setScriptsReady] = useState(false);
    const [scriptError, setScriptError] = useState<string | null>(null);

    /* -----------------------------------------------------------------------
     * Helper: tear down a viewer instance fully — Potree has no public
     * dispose(), but its render loop keeps running unless we explicitly stop
     * it. Without this, swapping point clouds leaks WebGL contexts and old
     * worker threads keep fetching nodes in the background.
     * --------------------------------------------------------------------- */
    function disposePotreeViewer(viewer: any) {
      if (!viewer || viewer.__appDisposed) return;
      viewer.__appDisposed = true;
      try { viewer?.renderer?.setAnimationLoop?.(null); } catch { /* noop */ }
      try { viewer?.inputHandler?.dispose?.(); } catch { /* noop */ }
      try { viewer?.measuringTool?.dispose?.(); } catch { /* noop */ }
      try {
        const scene = viewer?.scene;
        const pcs = Array.isArray(scene?.pointclouds) ? [...scene.pointclouds] : [];
        for (const pc of pcs) {
          try { scene?.removePointCloud?.(pc); } catch { /* noop */ }
          try { pc?.material?.dispose?.(); } catch { /* noop */ }
          try { pc?.pcoGeometry?.dispose?.(); } catch { /* noop */ }
        }
        if (Array.isArray(scene?.pointclouds)) scene.pointclouds.length = 0;
      } catch { /* noop */ }
      try { viewer?.renderer?.dispose?.(); } catch { /* noop */ }
      try { viewer?.renderer?.forceContextLoss?.(); } catch { /* noop */ }
      try { viewer?.renderer?.domElement?.remove?.(); } catch { /* noop */ }
      try { window?.Potree?.lru?.freeMemory?.(); } catch { /* noop */ }
    }

    /* -----------------------------------------------------------------------
     * Helper: apply orbit / earth / fly control scheme to a viewer.
     * --------------------------------------------------------------------- */
    function applyNavigationMode(viewer: any, mode: PotreeNavigationMode) {
      if (!viewer || typeof viewer.setControls !== "function") return;
      try {
        if (mode === "orbit" && viewer.orbitControls) {
          viewer.setControls(viewer.orbitControls);
        } else if (mode === "earth" && viewer.earthControls) {
          viewer.setControls(viewer.earthControls);
        } else if (mode === "fly" && viewer.fpControls) {
          viewer.setControls(viewer.fpControls);
          // Fly mode: allow elevation changes
          if (Object.prototype.hasOwnProperty.call(viewer.fpControls, "lockElevation")) {
            viewer.fpControls.lockElevation = false;
          }
        }
      } catch { /* noop */ }
    }

    /* -----------------------------------------------------------------------
     * Marker / line helpers (used by the imperative handle below).
     * Markers are sprites with a glow texture; lines are plain Line2 segments.
     * Both belong to dedicated groups so we can find them on raycast and so
     * disposal can wipe them as a unit.
     * --------------------------------------------------------------------- */
    /** Minimal tag marker — a small solid disc with a thin white outline.
     *  Deliberately plainer than the digital-twin viewer's sci-fi reticle:
     *  the point cloud module wants the marker to read as "a tagged spot
     *  on the data" without competing with the geometry itself. */
    function makeMarkerTexture(colorHex: number) {
      const THREE = window.THREE;
      const size = 64;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return new THREE.CanvasTexture(canvas);
      const cx = size / 2;
      const cy = size / 2;
      const r = (colorHex >> 16) & 0xff;
      const g = (colorHex >> 8) & 0xff;
      const b = colorHex & 0xff;
      // Filled colored disc.
      ctx.fillStyle = `rgba(${r},${g},${b},0.95)`;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 0.36, 0, Math.PI * 2);
      ctx.fill();
      // Thin contrasting outline so the dot reads against any background.
      ctx.strokeStyle = "rgba(255,255,255,0.85)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 0.36, 0, Math.PI * 2);
      ctx.stroke();

      const tex = new THREE.CanvasTexture(canvas);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.needsUpdate = true;
      return tex;
    }

    function ensureGroups() {
      const viewer = viewerRef.current;
      const THREE = window.THREE;
      if (!viewer || !THREE) return;
      if (!markersGroupRef.current) {
        const g = new THREE.Group();
        g.name = "app-markers";
        viewer.scene.scene.add(g);
        markersGroupRef.current = g;
      }
      if (!linesGroupRef.current) {
        const g = new THREE.Group();
        g.name = "app-lines";
        viewer.scene.scene.add(g);
        linesGroupRef.current = g;
      }
    }

    /* -----------------------------------------------------------------------
     * Load Potree scripts once, then build / rebuild the viewer whenever the
     * viewUrl changes.
     * --------------------------------------------------------------------- */
    useEffect(() => {
      let cancelled = false;
      loadPotree()
        .then(() => { if (!cancelled) setScriptsReady(true); })
        .catch((err) => {
          if (cancelled) return;
          const msg = err instanceof Error ? err.message : "Failed to load Potree";
          setScriptError(msg);
          onLoadErrorRef.current?.(msg);
        });
      return () => { cancelled = true; };
    }, []);

    useEffect(() => {
      if (!scriptsReady || !renderAreaRef.current || !viewUrl) return;

      const Potree = window.Potree;
      const THREE = window.THREE;
      const renderArea = renderAreaRef.current;
      readyFiredRef.current = false;
      // Reset HTML in case a previous viewer left children behind.
      renderArea.innerHTML = "";

      const viewer = new Potree.Viewer(renderArea, { noDragAndDrop: true });
      window.viewer = viewer;
      viewerRef.current = viewer;

      try {
        viewer.setFOV(60);
        viewer.setPointBudget(pointBudget);
        viewer.setEDLEnabled(edlEnabled);
        try { viewer.setEDLRadius(1.4); } catch { /* noop */ }
        try { viewer.setEDLStrength(0.4); } catch { /* noop */ }
        try { viewer.setEDLOpacity(1.0); } catch { /* noop */ }
        viewer.setBackground("gradient");
      } catch { /* noop */ }

      applyNavigationMode(viewer, navigationModeRef.current);

      // Streaming progress: Potree's loader doesn't expose a per-chunk hook
      // for v2, so we wrap fetch on this viewer's load to report
      // metadata.json + binary streams to the parent. Coarse but enough to
      // drive a "Loading…" UX. The viewer itself doesn't need this; only the
      // parent's overlay does.
      let totalBytes = 0;
      let loadedBytes = 0;
      const wrappedFetch = window.fetch.bind(window);
      const originalFetch = window.fetch;
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const res = await wrappedFetch(input, init);
        const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
        if (url.includes("/point-clouds/") && url.includes("/files/")) {
          const lenHeader = res.headers.get("Content-Length");
          if (lenHeader) {
            totalBytes += Number(lenHeader) || 0;
            // Without a per-chunk reader we approximate "loaded" as
            // length-complete on response receipt. Good enough for UX.
            const clone = res.clone();
            clone.arrayBuffer().then((buf) => {
              loadedBytes += buf.byteLength;
              onLoadProgressRef.current?.(loadedBytes, totalBytes);
            }).catch(() => { /* ignore */ });
          }
        }
        return res;
      };

      // Kick off load. Potree v2 (metadata.json) is auto-detected by the URL.
      try {
        Potree.loadPointCloud(viewUrl, "asset-radar-pc", (e: any) => {
          if (viewerRef.current !== viewer) return; // stale callback
          if (!e || !e.pointcloud) {
            onLoadErrorRef.current?.("Point cloud failed to load");
            return;
          }
          pointcloudRef.current = e.pointcloud;
          try {
            const material = e.pointcloud.material;
            if (material && Potree.PointSizeType && Potree.PointShape) {
              material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
              material.size = 1;
              material.minSize = 1;
              material.maxSize = 10;
              material.shape = Potree.PointShape.CIRCLE;
              try {
                const hasRGBA = !!(e.pointcloud.getAttribute && e.pointcloud.getAttribute("rgba"));
                const hasRGB = !!(e.pointcloud.getAttribute && e.pointcloud.getAttribute("rgb"));
                if (hasRGBA) material.activeAttributeName = "rgba";
                else if (hasRGB) material.activeAttributeName = "rgb";
              } catch { /* noop */ }
            }
            viewer.scene.addPointCloud(e.pointcloud);
            viewer.fitToScreen();
            ensureGroups();
            if (!readyFiredRef.current) {
              readyFiredRef.current = true;
              onReadyRef.current?.();
            }
          } catch (err) {
            onLoadErrorRef.current?.(err instanceof Error ? err.message : "Point cloud setup failed");
          }
        });
      } catch (err) {
        onLoadErrorRef.current?.(err instanceof Error ? err.message : "loadPointCloud threw");
      }

      /* ---------------------------------------------------------------------
       * Pointer-up handler: marker hit takes priority, otherwise raycast the
       * cloud. Mirrors SparkViewer's contract so DigitalTwinViewer-style
       * tools (tag / measure) work identically against either viewer.
       * ------------------------------------------------------------------- */
      let downX = 0;
      let downY = 0;
      let downAt = 0;
      function onPointerDown(e: PointerEvent) {
        downX = e.clientX;
        downY = e.clientY;
        downAt = Date.now();
      }
      function onPointerUp(e: PointerEvent) {
        // Treat as a click only if pointer hasn't moved much and wasn't held
        // (long-press counts as drag). Avoids placing tags during orbits.
        const dx = Math.abs(e.clientX - downX);
        const dy = Math.abs(e.clientY - downY);
        const dt = Date.now() - downAt;
        if (dx > 4 || dy > 4 || dt > 500) return;
        // Translate the page coords to canvas-local pixels.
        const canvas = viewer.renderer?.domElement;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const pixelX = e.clientX - rect.left;
        const pixelY = e.clientY - rect.top;
        // Marker hit test (priority).
        const markersGroup = markersGroupRef.current;
        if (markersGroup) {
          const mouseNdc = new THREE.Vector2(
            (pixelX / rect.width) * 2 - 1,
            -(pixelY / rect.height) * 2 + 1,
          );
          const raycaster = new THREE.Raycaster();
          const cam = viewer.scene.getActiveCamera ? viewer.scene.getActiveCamera() : viewer.scene.cameraP;
          raycaster.setFromCamera(mouseNdc, cam);
          const hits = raycaster.intersectObjects(markersGroup.children, false);
          if (hits.length > 0) {
            onMarkerPickRef.current?.(hits[0].object);
            return;
          }
        }
        // Point cloud raycast.
        if (!pointcloudRef.current) {
          onPickRef.current?.(null, { x: pixelX, y: pixelY });
          return;
        }
        try {
          const cam = viewer.scene.getActiveCamera ? viewer.scene.getActiveCamera() : viewer.scene.cameraP;
          const mouseVec = new THREE.Vector2(pixelX, pixelY);
          const I = Potree.Utils.getMousePointCloudIntersection(
            mouseVec, cam, viewer, viewer.scene.pointclouds,
          );
          if (I && I.location) {
            onPickRef.current?.(I.location.clone(), { x: pixelX, y: pixelY });
          } else {
            onPickRef.current?.(null, { x: pixelX, y: pixelY });
          }
        } catch {
          onPickRef.current?.(null, { x: pixelX, y: pixelY });
        }
      }

      const canvasEl = renderArea;
      canvasEl.addEventListener("pointerdown", onPointerDown, true);
      canvasEl.addEventListener("pointerup", onPointerUp, true);

      // Cleanup on unmount or viewUrl change.
      return () => {
        canvasEl.removeEventListener("pointerdown", onPointerDown, true);
        canvasEl.removeEventListener("pointerup", onPointerUp, true);
        // Restore the original fetch so other parts of the SPA aren't
        // affected after the viewer unmounts.
        window.fetch = originalFetch;
        disposePotreeViewer(viewer);
        if (window.viewer === viewer) {
          window.viewer = undefined;
        }
        viewerRef.current = null;
        pointcloudRef.current = null;
        markersGroupRef.current = null;
        linesGroupRef.current = null;
        try { renderArea.innerHTML = ""; } catch { /* noop */ }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scriptsReady, viewUrl]);

    // Reapply navigation mode when the prop changes (without rebuilding the viewer).
    useEffect(() => {
      navigationModeRef.current = navigationMode;
      const viewer = viewerRef.current;
      if (viewer) applyNavigationMode(viewer, navigationMode);
    }, [navigationMode]);

    /* -----------------------------------------------------------------------
     * Imperative handle — these are the methods PointCloudViewer.tsx will
     * call to place tags, draw measure lines, animate the camera, etc.
     * --------------------------------------------------------------------- */
    useImperativeHandle(ref, () => ({
      pickAtPixel(pixelX, pixelY) {
        const viewer = viewerRef.current;
        if (!viewer || !pointcloudRef.current || !window.Potree || !window.THREE) return null;
        try {
          const cam = viewer.scene.getActiveCamera ? viewer.scene.getActiveCamera() : viewer.scene.cameraP;
          const mouseVec = new window.THREE.Vector2(pixelX, pixelY);
          const I = window.Potree.Utils.getMousePointCloudIntersection(
            mouseVec, cam, viewer, viewer.scene.pointclouds,
          );
          return I?.location ? I.location.clone() : null;
        } catch { return null; }
      },
      addPointMarker(worldPos, color = 0xa855f7, options) {
        ensureGroups();
        const THREE = window.THREE;
        const shape = options?.shape ?? "sprite";
        // Scene-radius-relative scale so markers stay legible across very
        // different cloud sizes (small BLK indoor scan vs. mobile LiDAR).
        const viewer = viewerRef.current;
        const radius = viewer?.scene?.view?.radius || 5;
        const s = Math.max(0.2, radius * 0.012);

        if (shape === "cube") {
          // Asset tags: colored box + white edge lines wrapped in a Group
          // so the caller still removes a single object. Edges read as 3D
          // even against bright point-cloud regions; depthTest:false
          // overlays the cloud rather than getting occluded by it.
          const wrapper = new THREE.Group();
          const boxGeom = new THREE.BoxGeometry(s, s, s);
          const boxMat = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false,
          });
          const box = new THREE.Mesh(boxGeom, boxMat);
          box.renderOrder = 999;
          wrapper.add(box);
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(boxGeom),
            new THREE.LineBasicMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 0.85,
              depthTest: false,
            }),
          );
          edges.renderOrder = 1000;
          wrapper.add(edges);
          wrapper.position.copy(worldPos);
          markersGroupRef.current.add(wrapper);
          return wrapper;
        }

        if (shape === "sphere") {
          // Sensor tags: colored sphere + soft white backside outline.
          const wrapper = new THREE.Group();
          const sphGeom = new THREE.SphereGeometry(s * 0.6, 24, 16);
          const sphMat = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false,
          });
          const sphere = new THREE.Mesh(sphGeom, sphMat);
          sphere.renderOrder = 999;
          wrapper.add(sphere);
          const outline = new THREE.Mesh(
            new THREE.SphereGeometry(s * 0.65, 24, 16),
            new THREE.MeshBasicMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 0.35,
              depthTest: false,
              depthWrite: false,
              side: THREE.BackSide,
            }),
          );
          outline.renderOrder = 998;
          wrapper.add(outline);
          wrapper.position.copy(worldPos);
          markersGroupRef.current.add(wrapper);
          return wrapper;
        }

        // Default: canvas-painted sprite reticle. Used by the measure tool.
        const map = makeMarkerTexture(color);
        const material = new THREE.SpriteMaterial({ map, depthTest: false, depthWrite: false, transparent: true });
        const sprite = new THREE.Sprite(material);
        sprite.position.copy(worldPos);
        sprite.scale.set(s, s, 1);
        sprite.renderOrder = 999;
        markersGroupRef.current.add(sprite);
        return sprite;
      },
      removeMarker(obj) {
        try {
          markersGroupRef.current?.remove(obj);
          // Walk the subtree: cube/sphere markers are Groups; sprite
          // markers are leaf Sprites. Dispose geometry/material/texture
          // exactly once each so repeated tag refreshes don't leak.
          if (typeof obj?.traverse === "function") {
            obj.traverse((child: any) => {
              child.geometry?.dispose?.();
              const mat = child.material;
              if (Array.isArray(mat)) {
                mat.forEach((m: any) => { m?.map?.dispose?.(); m?.dispose?.(); });
              } else if (mat) {
                mat.map?.dispose?.();
                mat.dispose?.();
              }
            });
          } else {
            (obj as any).material?.map?.dispose?.();
            (obj as any).material?.dispose?.();
          }
        } catch { /* noop */ }
      },
      addLine(a, b, color = 0x10b981) {
        ensureGroups();
        const THREE = window.THREE;
        const geom = new THREE.BufferGeometry().setFromPoints([a.clone(), b.clone()]);
        const mat = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true });
        const line = new THREE.Line(geom, mat);
        line.renderOrder = 998;
        linesGroupRef.current.add(line);
        return line;
      },
      removeLine(line) {
        try {
          linesGroupRef.current?.remove(line);
          (line as any).geometry?.dispose?.();
          (line as any).material?.dispose?.();
        } catch { /* noop */ }
      },
      recenter() {
        try { viewerRef.current?.fitToScreen(); } catch { /* noop */ }
      },
      zoomIn() {
        const viewer = viewerRef.current;
        if (!viewer?.scene?.view) return;
        const view = viewer.scene.view;
        view.radius = Math.max(0.1, (view.radius || 10) * 0.6);
        try { viewer.setMoveSpeed?.(view.radius / 2.5); } catch { /* noop */ }
      },
      zoomOut() {
        const viewer = viewerRef.current;
        if (!viewer?.scene?.view) return;
        const view = viewer.scene.view;
        view.radius = (view.radius || 10) * 1.5;
        try { viewer.setMoveSpeed?.(view.radius / 2.5); } catch { /* noop */ }
      },
      zoomToPoint(point) {
        const viewer = viewerRef.current;
        const THREE = window.THREE;
        if (!viewer || !THREE || !point) return;
        try {
          const view = viewer.scene.view;
          const dir = view.direction.clone ? view.direction.clone() : new THREE.Vector3(0, -1, 0);
          dir.multiplyScalar(-1);
          const r = view.radius > 0 ? Math.min(view.radius, 5) : 5;
          const target = new THREE.Vector3().addVectors(point, dir.multiplyScalar(r));
          view.position.copy(target);
          view.lookAt(point);
          view.radius = r;
          try { viewer.setMoveSpeed?.(r / 2.5); } catch { /* noop */ }
        } catch { /* noop */ }
      },
      highlightMarker(obj, options) {
        const dur = options?.durationMs ?? 600;
        const scaleUp = options?.scale ?? 1.8;
        if (!obj || !obj.scale) return;
        const original = obj.scale.x;
        const start = performance.now();
        function tick(now: number) {
          const t = Math.min(1, (now - start) / dur);
          const factor = 1 + (scaleUp - 1) * (1 - Math.abs(2 * t - 1));
          obj.scale.set(original * factor, original * factor, 1);
          if (t < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      },
      setFov(degrees) {
        const viewer = viewerRef.current;
        if (!viewer) return;
        try {
          viewer.setFOV(degrees);
          const camP = viewer.scene?.cameraP;
          if (camP) {
            camP.fov = degrees;
            camP.updateProjectionMatrix?.();
          }
        } catch { /* noop */ }
      },
      getFov() {
        const viewer = viewerRef.current;
        if (!viewer) return 60;
        try { return viewer.scene?.cameraP?.fov ?? viewer.getFOV?.() ?? 60; } catch { return 60; }
      },
      projectToScreen(point) {
        const viewer = viewerRef.current;
        const THREE = window.THREE;
        const canvas = viewer?.renderer?.domElement;
        if (!viewer || !THREE || !canvas) return { x: 0, y: 0, visible: false };
        try {
          const cam = viewer.scene.getActiveCamera ? viewer.scene.getActiveCamera() : viewer.scene.cameraP;
          const v = point.clone ? point.clone() : new THREE.Vector3(point.x, point.y, point.z);
          v.project(cam);
          const visible = v.z < 1 && v.z > -1 && v.x > -1 && v.x < 1 && v.y > -1 && v.y < 1;
          const rect = canvas.getBoundingClientRect();
          const x = (v.x + 1) * 0.5 * rect.width;
          const y = (1 - (v.y + 1) * 0.5) * rect.height;
          return { x, y, visible };
        } catch { return { x: 0, y: 0, visible: false }; }
      },
      startMeasurement(type) {
        const viewer = viewerRef.current;
        if (!viewer?.measuringTool || typeof viewer.measuringTool.startInsertion !== "function") {
          return null;
        }
        // Options chosen to match what each measurement *means* — a Point
        // shows just coordinates, Distance shows segment lengths, Area
        // closes the polygon and computes its area, etc. Mirrors the
        // legacy PotreeToolBar config so behavior is identical to what
        // the user already knows from that build.
        const opts: Record<PotreeMeasureType, Record<string, unknown>> = {
          point: {
            showDistances: false, showAngles: false, showCoordinates: true,
            showArea: false, closed: true, maxMarkers: 1, name: "Point",
          },
          distance: {
            showDistances: true, showArea: false, closed: false, name: "Distance",
          },
          height: {
            showDistances: false, showHeight: true, showArea: false,
            closed: false, maxMarkers: 2, name: "Height",
          },
          area: {
            showDistances: true, showArea: true, closed: true, name: "Area",
          },
          angle: {
            showDistances: false, showAngles: true, showArea: false,
            closed: true, maxMarkers: 3, name: "Angle",
          },
        };
        try {
          return viewer.measuringTool.startInsertion(opts[type]);
        } catch { return null; }
      },
      clearMeasurements() {
        const viewer = viewerRef.current;
        try { viewer?.scene?.removeAllMeasurements?.(); } catch { /* noop */ }
      },
      setCameraView(dir) {
        // Rotate around the currently focused point rather than calling
        // Potree's `set*View()`, which refits the full point-cloud bounds
        // and yanks the camera out of any zoomed-in inspection. This
        // mirrors the PotreeToolBar reference implementation.
        const viewer = viewerRef.current;
        const view = viewer?.scene?.view;
        if (!view || typeof view.setView !== "function") return;
        const directions: Record<PotreeCameraView, { x: number; y: number; z: number }> = {
          top:    { x: 0, y: 0, z: -1 },
          bottom: { x: 0, y: 0, z: 1 },
          left:   { x: -1, y: 0, z: 0 },
          right:  { x: 1, y: 0, z: 0 },
          front:  { x: 0, y: 1, z: 0 },
          back:   { x: 0, y: -1, z: 0 },
        };
        const d = directions[dir];
        if (!d) return;
        try {
          const pos = view.position || {};
          const currentRadius = Number.isFinite(view.radius) && view.radius > 0 ? view.radius : 1;
          // Focus point: prefer the active pivot (so the user's pan target
          // is preserved); fall back to the camera's current look-at
          // (position + direction*radius) so we never refit the whole bound.
          let focus: { x: number; y: number; z: number };
          try {
            const p = typeof view.getPivot === "function" ? view.getPivot() : null;
            if (p && Number.isFinite(p.x)) {
              focus = { x: p.x, y: p.y, z: p.z };
            } else {
              const dir2 = view.direction || { x: 0, y: 0, z: 0 };
              focus = {
                x: (pos.x || 0) + (dir2.x || 0) * currentRadius,
                y: (pos.y || 0) + (dir2.y || 0) * currentRadius,
                z: (pos.z || 0) + (dir2.z || 0) * currentRadius,
              };
            }
          } catch {
            focus = { x: 0, y: 0, z: 0 };
          }
          const dist = Math.hypot(
            (pos.x || 0) - focus.x,
            (pos.y || 0) - focus.y,
            (pos.z || 0) - focus.z,
          ) || currentRadius;
          const newPos = {
            x: focus.x - d.x * dist,
            y: focus.y - d.y * dist,
            z: focus.z - d.z * dist,
          };
          view.setView(
            [newPos.x, newPos.y, newPos.z],
            [focus.x, focus.y, focus.z],
            0,
          );
          view.radius = dist;
          try { viewer.setMoveSpeed?.(Math.max(dist / 2.5, 1e-6)); } catch { /* noop */ }
        } catch { /* noop */ }
      },
      toggleProjection() {
        const viewer = viewerRef.current;
        const Potree = window.Potree;
        if (!viewer || !Potree?.CameraMode) return;
        const cam = viewer.scene?.getActiveCamera
          ? viewer.scene.getActiveCamera()
          : viewer.scene?.cameraP;
        const isPerspective = !!cam?.isPerspectiveCamera;
        const next = isPerspective ? Potree.CameraMode.ORTHOGRAPHIC : Potree.CameraMode.PERSPECTIVE;
        try {
          if (typeof viewer.setCameraMode === "function") viewer.setCameraMode(next);
          else if (viewer.scene) viewer.scene.cameraMode = next;
        } catch { /* noop */ }
      },
      getCameraProjection() {
        const viewer = viewerRef.current;
        try {
          const cam = viewer?.scene?.getActiveCamera
            ? viewer.scene.getActiveCamera()
            : viewer?.scene?.cameraP;
          return cam?.isOrthographicCamera ? "orthographic" : "perspective";
        } catch {
          return "perspective";
        }
      },
    }), []);

    if (scriptError) {
      return (
        <div className={(className || "") + " flex items-center justify-center bg-surface-950 text-sm text-red-300"}>
          {scriptError}
        </div>
      );
    }

    return (
      <div className={(className || "") + " potree_container"} style={{ position: "relative" }}>
        <div
          id="potree_render_area"
          ref={renderAreaRef}
          style={{ position: "absolute", inset: 0, background: "transparent" }}
        />
      </div>
    );
  },
);
